import React, { useMemo } from 'react'
import { useTable, usePagination, useFilters } from 'react-table'
import {
    Flex, Table as ChakraTable, Tbody, Td, Text, Th, Thead, Tr,
    Button, ButtonGroup, Spacer, FormControl, Select, SimpleGrid,
    useColorModeValue as colorValue, useBreakpointValue as breakptValue,
    TableContainer, Stack
} from '@chakra-ui/react'
import { BsChevronBarLeft, BsChevronLeft, BsChevronRight, BsChevronBarRight } from "react-icons/bs";
import ReactSelect from 'react-select'

const EmptyRow = ({ colSpan, text, rowSize = "100px" }) => {
    return (
        <Tr bgColor={colorValue("gray.100", "gray.600")} >
            <Td colSpan={colSpan} >
                <Flex w="full" justify="center" py={rowSize}>
                    <Text fontSize="sm">{text || "ไม่พบข้อมูล"}</Text>
                </Flex>
            </Td>
        </Tr>

    )
}

// This is a custom filter UI for selecting
// a unique option from a list
export function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}) {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = useMemo(() => {
        const options = new Set()
        preFilteredRows.forEach(row => {
            options.add(row.values[id])
        })
        return [...options.values()]
    }, [id, preFilteredRows])

    // Render a multi-select box
    return (
        <select
            value={filterValue}
            onChange={e => {
                setFilter(e.target.value || undefined)
            }}
        >
            <option value="">All</option>
            {options.map((option, i) => (
                <option key={i} value={option}>
                    {option}
                </option>
            ))}
        </select>
    )
}

const defaultOptions = {
    pageSizes: [10, 50, 100],
    showPagination: true,
    emptyRowSize: "100px"
}
const Table = ({ columns, data, options, size = "sm", variant = "simple", ...rest }) => {
    // console.log('table')
    // console.log(data)
    if (options) {
        options = { ...defaultOptions, ...options }
    } else {
        options = defaultOptions
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0 },
        },
        useFilters,
        usePagination
    )

    const countCols = () => {
        const hgLen = headerGroups.length
        if (hgLen > 0) {
            return headerGroups[hgLen - 1].headers.length
        }
        return 0
    }

    const pageSelectOption = pageOptions.map(p => {
        return { value: p, label: p + 1 }
    })

    return (
        <Flex w="full" direction="column">
            <TableContainer>
                <ChakraTable {...getTableProps()} size={size} variant={variant} {...rest}>
                    <Thead>
                        {
                            headerGroups.map(headerGroup => (
                                <Tr {...headerGroup.getHeaderGroupProps()}>
                                    {
                                        headerGroup.headers.map(column => (
                                            <Th {...column.getHeaderProps()}>
                                                <Flex>
                                                    {column.render('Header')}
                                                </Flex>
                                                <Flex>{column.Filter ? column.render('Filter') : null}</Flex>
                                            </Th>
                                        ))
                                    }
                                </Tr>
                            ))
                        }
                    </Thead>
                    <Tbody {...getTableBodyProps()}>
                        {
                            page?.map(row => {
                                prepareRow(row)
                                return (
                                    <Tr {...row.getRowProps()} _hover={variant === "simple" && { bgColor: "blue.50" }}>
                                        {
                                            row.cells.map(cell => {
                                                return (
                                                    <Td {...cell.getCellProps()}>
                                                        {cell.render('Cell')}
                                                    </Td>
                                                )
                                            })
                                        }
                                    </Tr>
                                )
                            })
                        }
                        {page?.length === 0 && <EmptyRow colSpan={countCols()} rowSize={options?.emptyRowSize} />}
                    </Tbody>
                </ChakraTable>
            </TableContainer>
            <Stack mt={3} display={options.showPagination ? 'flex' : 'none'} align="center" direction={{ base: 'column', md: 'row' }}>
                <Flex>
                    <Select
                        value={pageSize}
                        onChange={e => {
                            setPageSize(Number(e.target.value))
                        }}
                    >
                        {options.pageSizes.map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                แสดง {pageSize} รายการ
                            </option>
                        ))}
                    </Select>
                </Flex>
                <ButtonGroup isAttached>
                    <Button leftIcon={<BsChevronBarLeft />} onClick={() => gotoPage(0)} disabled={!canPreviousPage} />
                    <Button leftIcon={<BsChevronLeft />} onClick={() => previousPage()} disabled={!canPreviousPage} />
                    <Flex align="center" mx={3}>
                        <Text>
                            { breakptValue({ base: "", md: "หน้าที่" }) }
                            <Text as="strong" ml={2}>
                                {pageIndex + 1} / {pageOptions?.length}
                            </Text>
                        </Text>
                    </Flex>
                    <Button leftIcon={<BsChevronRight />} onClick={() => nextPage()} disabled={!canNextPage} />
                    <Button leftIcon={<BsChevronBarRight />} onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} />
                </ButtonGroup>
                <Spacer />
                <Flex>
                    <FormControl>
                        <SimpleGrid columns={2} alignItems="center" w="240px" spacing={2}>
                            <Text display="flex" justifyContent="flex-end">{ breakptValue({ base: "ไปยัง", md: "ไปยังหน้าที่" }) }</Text>
                            <ReactSelect
                                isClearable
                                value={{ value: pageIndex, label: pageIndex + 1 }}
                                onChange={e => {
                                    if (e) gotoPage(e.value)
                                    else gotoPage(0)
                                }}
                                options={pageSelectOption}
                            />
                        </SimpleGrid>
                    </FormControl>
                </Flex>
            </Stack>
        </Flex >
    )
}

export default Table
